import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import { guestListData } from '@data-ts/pages/guest-list';
import {
  AnimationContainer,
  EdiitingGuestListWrapper,
  SpreadsheetColumn,
  SpreadsheetColumns,
  MultipleTextPairing,
  SpreadSheetTextPairing,
  StyledCloudImage,
  StyledBox,
  ImportButtonBox,
  SpreadsheetCloudImage,
  MultipleEditorBox,
  EditorsCloudImage,
  StyledCursorsCloudImage,
} from './EditingGuestlist.styles';
import HtmlParser from 'react-html-parser';

import { SpacingStack } from '@components/SpacingStack';
import { Box, BoxProps } from '@components/Box';
import { Columns } from '@components/Columns';

interface EditingGuestlistProps
  extends Readonly<{
    backgroundColor: BoxProps['backgroundColor'];
  }> {}

export const EditingGuestlist: React.FC<EditingGuestlistProps> = ({ backgroundColor }) => {
  const editingData = guestListData.editing;
  const sectionId = 'Editing_Guest_List';
  const sectionRef = useRef<HTMLDivElement>(null);

  const multipleEditorsSectionId = 'Guest_List_Multiple_Editors';
  const multipleEditorsSectionRef = useRef<HTMLDivElement>(null);
  const spreadSheetImportExportSectionRef = useRef<HTMLDivElement>(null);

  const scrollingTextRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const exportId = 'Export_Section';
  const cursorsId = 'Cursors_Section';

  useLayoutEffect(() => {
    let exportScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let exportTimeline: ReturnType<typeof gsap.timeline>;
    if (scrollController && isMounted) {
      // Import Button animation
      exportTimeline = gsap.timeline({ paused: true });
      exportTimeline.to(`#${exportId}`, { y: '50%' }, 0);
      exportScene = new ScrollMagic.Scene({
        triggerElement: `#Spreadsheet_Import_Export`,
        triggerHook: 1,
        duration: '100%',
        offset: 400,
      })
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          exportTimeline?.progress(event.progress);
        });
    }

    let editorScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let editorTimeline: ReturnType<typeof gsap.timeline>;
    if (scrollController && isMounted) {
      // Import Button animation
      editorTimeline = gsap.timeline({ paused: true });
      editorTimeline.to(`#${cursorsId}`, { y: '18%' }, 0);
      editorScene = new ScrollMagic.Scene({
        triggerElement: `#${multipleEditorsSectionId}`,
        triggerHook: 1,
        duration: '100%',
        offset: 400,
      })
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          editorTimeline?.progress(event.progress);
        });
    }

    const clearScene = () => {
      if (exportTimeline) {
        exportTimeline.progress(0);
        exportTimeline.kill();
      }
      if (editorTimeline) {
        editorTimeline.progress(0);
        editorTimeline.kill();
      }
      if (exportScene) {
        exportScene.destroy(true);
      }
      if (editorScene) {
        editorScene.destroy(true);
      }
    };

    return clearScene;
  }, [scrollController, scrollingTextRef, isMounted]);

  return (
    <EdiitingGuestListWrapper backgroundColor={backgroundColor} paddingY={[10, 10, 12]} paddingX={0} ref={sectionRef} id={sectionId}>
      <SpacingStack spacing={[8, 12, 12]}>
        {/* Without div wrapper, spacing stack pushes this set of columns to the left */}
        <div id={multipleEditorsSectionId} ref={multipleEditorsSectionRef}>
          <Columns>
            <Columns.Column width="half">
              <Box marginRight={[0, 12]} id="EditingListSection">
                <AnimationContainer>
                  <StyledBox>
                    <EditorsCloudImage
                      alt={editingData.import.altText}
                      width="100%"
                      src="sitewide/block_multiple-editors/gl-multiple_editors.jpg"
                    />
                    <MultipleEditorBox id={cursorsId}>
                      <StyledCursorsCloudImage
                        alt={editingData.import.altText}
                        width="100%"
                        src="sitewide/block_multiple-editors/cursors.png"
                      />
                    </MultipleEditorBox>
                  </StyledBox>
                </AnimationContainer>
              </Box>
            </Columns.Column>
            <Columns.Column width="half">
              <Box marginTop={[8, 11, 11]}>
                <MultipleTextPairing
                  alignment="left"
                  eyebrow={editingData.edit.eyebrow}
                  eyebrowColor="humanaDark4"
                  eyebrowVariant="sectionEyeBrow24"
                  eyebrowTagName="h3"
                  title={editingData.edit.title}
                  titleColor="neutra6"
                  titleVariant="hed56"
                  titleTagName="h3"
                  body={editingData.edit.content}
                  bodyVariant="dek20"
                  bodyMaxWidth="390px"
                  bodyColor="neutra5"
                />
              </Box>
            </Columns.Column>
          </Columns>
        </div>
        <div id="Spreadsheet_Import_Export" ref={spreadSheetImportExportSectionRef}>
          <SpreadsheetColumns>
            <SpreadsheetColumn width="half">
              <StyledBox>
                <SpreadsheetCloudImage
                  alt={editingData.import.altText}
                  width="100%"
                  src="guest-list/spreadsheet-import-export/spreadsheet-import-v2.jpg"
                />
                <ImportButtonBox id={exportId}>
                  <StyledCloudImage alt={editingData.import.altText} width="100%" src="guest-list/spreadsheet-import-export/import.png" />
                </ImportButtonBox>
              </StyledBox>
            </SpreadsheetColumn>
            <SpreadsheetColumn width="half">
              <Box marginTop={[4, 7, 7]}>
                <SpreadSheetTextPairing
                  alignment="left"
                  eyebrow={editingData.import.eyebrow}
                  eyebrowColor="humanaDark4"
                  eyebrowVariant="sectionEyeBrow24"
                  eyebrowTagName="h3"
                  title={editingData.import.title}
                  titleColor="neutra6"
                  titleVariant="hed56"
                  titleTagName="h3"
                  body={HtmlParser(editingData.import.content)}
                  bodyVariant="dek20"
                  bodyMaxWidth="380px"
                  bodyColor="neutra5"
                />
              </Box>
            </SpreadsheetColumn>
          </SpreadsheetColumns>
        </div>
      </SpacingStack>
    </EdiitingGuestListWrapper>
  );
};
